import React, { useState, useRef, useEffect } from 'react';
import RawHtml from '../RawHtml';
import PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';

import classNames from 'classnames';
import useMediaQuery from '../../utils/useMediaQuery';
import { breakpoints } from '../../constants';
import AnchorLink from '../AnchorLink';

import styles from './ShortFacts.module.scss';

const ShortFacts = ({ id, heading, anchorLink, content }) => {
    const contentId = `facts-${id}`;

    const isDesktop = useMediaQuery({
        query: `(min-width: ${breakpoints.l}px)`,
    });

    const [expanded, setExpanded] = useState(false);
    const contentContainerRef = useRef(null);

    const toggleExpanded = () => setExpanded(!expanded);

    // https://www.w3.org/WAI/GL/wiki/Using_the_WAI-ARIA_aria-expanded_state_to_mark_expandable_and_collapsible_regions#Example_1:_Using_a_button_to_collapse_and_expand_a_region
    useEffect(() => {
        if (expanded) {
            contentContainerRef.current.focus();
        }
    }, [expanded]);

    const HeadingElement = isDesktop ? 'h2' : 'button';

    const headingProps = !isDesktop
        ? {
              onClick: toggleExpanded,
              'aria-controls': contentId,
              'aria-expanded': expanded,
          }
        : {};

    const contentProps = !isDesktop
        ? {
              id: contentId,
              role: 'region',
              tabIndex: -1,
          }
        : {};

    const contentVisible = expanded || isDesktop;

    const classes = classNames(styles['ShortFacts'], {
        [styles['ShortFacts--Expanded']]: expanded,
        [styles['ShortFacts--Button']]: !isDesktop,
    });

    return (
        <div className={classes}>
            <div className={styles['ShortFacts__Container']}>
                <HeadingElement className={styles['ShortFacts__Heading']} {...headingProps}>
                    {isDesktop ? <AnchorLink title={heading} id={anchorLink} /> : heading}
                </HeadingElement>
                <AnimateHeight
                    duration={!isDesktop ? 350 : 1}
                    height={contentVisible ? 'auto' : 0}
                    className={styles['ShortFacts__AnimationWrapper']}
                    ref={contentContainerRef}
                    contentClassName={styles['ShortFacts__Content']}
                    {...contentProps}>
                    <RawHtml html={content} modifier="Inner" />
                </AnimateHeight>
            </div>
        </div>
    );
};

ShortFacts.propTypes = {
    id: PropTypes.string,
    heading: PropTypes.string,
    anchorLink: PropTypes.string,
    content: PropTypes.string,
};

ShortFacts.defaultProps = {
    id: '',
    heading: '',
    content: '',
};

export default ShortFacts;
